// iter 152 - Email-deeplink → Stripe checkout redirect.
//
// Lands users from the 5-email Cost-of-Living nurture sequence directly
// into a Stripe Checkout Session. Reads `lead_id` + optional `promo`
// from the URL, POSTs to `/api/tools/checkout/from-lead` (same
// endpoint the calculator results CTA uses), then sets
// `window.location.href` to the returned checkout_url. Renders a small
// "Redirecting to checkout…" panel while the round-trip is in flight.
// On any failure, falls back to the public Blueprint intake form so
// the user is never stranded on a dead screen.

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL || '';

const QuizCheckoutRedirect = () => {
  const [params] = useSearchParams();
  const leadId = params.get('lead_id') || '';
  const promo = params.get('promo') || '';
  const tier = (params.get('tier') || 'blueprint_only').toLowerCase();
  const [status, setStatus] = useState('redirecting'); // 'redirecting' | 'failed' | 'missing_lead'

  useEffect(() => {
    if (!leadId) {
      setStatus('missing_lead');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/tools/checkout/from-lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lead_id: leadId,
            tier,
            promo_code: promo || undefined,
          }),
        });
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (r.ok && data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        setStatus('failed');
      } catch {
        if (!cancelled) setStatus('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [leadId, promo, tier]);

  return (
    <div className="bg-[#F9F6EF] min-h-screen flex flex-col" data-testid="quiz-checkout-redirect-page">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="max-w-md w-full bg-white border border-[#EFE6CF] rounded-2xl p-8 text-center">
          {status === 'redirecting' && (
            <>
              <Loader2 className="w-7 h-7 text-[#1A5C6E] mx-auto animate-spin mb-4" />
              <h1 className="font-display italic text-2xl text-[#0A1F2E] mb-2">
                Taking you to checkout…
              </h1>
              <p className="text-sm text-[#0A1F2E]/70">
                Hang tight - we&rsquo;re creating your secure Stripe checkout session{promo ? ` with code ${promo}` : ''}.
              </p>
            </>
          )}
          {status === 'missing_lead' && (
            <>
              <h1 className="font-display italic text-2xl text-[#0A1F2E] mb-3">
                This link looks incomplete.
              </h1>
              <p className="text-sm text-[#0A1F2E]/70 mb-5">
                It&rsquo;s missing your lead reference. The easiest way forward is to start fresh from the calculator or the Blueprint intake.
              </p>
              <Link
                to="/mexico-cost-of-living-calculator"
                data-testid="quiz-checkout-fallback-calc"
                className="inline-block bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-semibold px-5 py-2.5 rounded-full text-sm"
              >
                Open the calculator
              </Link>
            </>
          )}
          {status === 'failed' && (
            <>
              <h1 className="font-display italic text-2xl text-[#0A1F2E] mb-3">
                We couldn&rsquo;t open checkout.
              </h1>
              <p className="text-sm text-[#0A1F2E]/70 mb-5">
                Likely your link expired. Click below to continue from the Blueprint intake - your offer{promo ? ` (${promo})` : ''} still works there.
              </p>
              <Link
                to={`/relocation/blueprint${promo ? `?promo=${promo}` : ''}`}
                data-testid="quiz-checkout-fallback-intake"
                className="inline-block bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-semibold px-5 py-2.5 rounded-full text-sm"
              >
                Continue to Blueprint
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default QuizCheckoutRedirect;
